<script>
  import { onMount } from 'svelte';
  import { selectedStudy, selectedSite, selectedParticipant } from '$stores/selectionStore';
  import { config, delayHours, studyProperties, fetchStudyProperties, isFeatureEnabled } from '$stores/configStore';
  import { glucoseunit } from '$stores/unitsStore';
  import { convertGlucoseValue } from '$utils/unitUtils';
  import { formatToLocalDateTime } from '$utils/dateUtils.js';
  import { exportToExcel, generateExportFilename } from '$utils/export.js';
  import StudyHeader from '$lib/StudyHeader.svelte';
  import ParticipantDetails from '$lib/ParticipantDetails.svelte';
  import SiteDetails from '$lib/SiteDetails.svelte';
  import FeatureInfo from '$lib/FeatureInfo.svelte';
  import Pagination from '$lib/Pagination.svelte';
  import Toolbar from '$lib/Toolbar.svelte';
  import Footer from '$lib/Footer.svelte';  import { FontAwesomeIcon } from '@fortawesome/svelte-fontawesome';
  import { faRotate, faFilter } from '@fortawesome/pro-light-svg-icons';
  import CgmStatsInfoCards from '$lib/CgmStatsInfoCards.svelte';
  import PrintButton from '$lib/PrintButton.svelte';

  const API_BASE_URL = '/api/proxy';

  let stats = [];
  let resultCount = 0;
  let currentPage = 1;
  let totalPages = 1;
  let limit = 20;
  let isLoading = true;
  let isExporting = false;
  let siteParticipants = [];
  let checkedRows = {};
  let showOnlyChecked = false;
  let selectedRow = null;
  let baselineRow = null;
  let lastStatsConfigKey = '';

  // Feature flag for CGM statistics report card view
  let isCgmStatisticsReportCardViewEnabled = true;

  // Reactive variable for feature flags
  $: {
    isCgmStatisticsReportCardViewEnabled = isFeatureEnabled('cardViews.cgmStatisticsReport');
  }

  onMount(async () => {
    if ($selectedStudy?._id) {
      await fetchStudyProperties($selectedStudy._id);
    }
    if ($selectedSite) {
      await handleSiteChange();
    }
  });

  // Handle study changes
  $: if ($selectedStudy?._id) {
    fetchStudyProperties($selectedStudy._id);
  }

  // Handle site changes
  $: if ($selectedSite) {
    handleSiteChange();
  } else {
    // Clear data when no site is selected
    siteParticipants = [];
    stats = [];
    resultCount = 0;
    selectedRow = null;
    baselineRow = null;
  }

  function getStatsConfigKey() {
    return JSON.stringify({
      studyId: $selectedStudy?._id ?? null,
      participantId: $selectedParticipant?._id ?? null,
      siteId: $selectedSite?._id ?? null,
      thresholds: $studyProperties?.glucoseParameters?.thresholds ?? null,
      events: $studyProperties?.glucoseParameters?.events ?? null
    });
  }

  // Re-fetch when study glucose settings change so in-range stats stay aligned
  $: if ($selectedSite && !isLoading) {
    const hasSubjects = !!$selectedParticipant || siteParticipants.length > 0;
    const currentConfigKey = getStatsConfigKey();
    if (hasSubjects && currentConfigKey !== lastStatsConfigKey) {
      fetchStats(1);
    }
  }

  async function handleSiteChange() {
    try {
      await fetchSiteParticipants();
      if (siteParticipants.length > 0) {
        await fetchStats(1);
        // Set initial selection to first row if available
        if (stats.length > 0) {
          selectedRow = stats[0];
        }
      }
    } catch (err) {
      console.error('Error handling site change:', err);
    }
  }

  async function fetchSiteParticipants() {
    try {
      if (!$selectedSite) return;

      const url = `${API_BASE_URL}/persons?page=1&pageSize=999999&siteGUID=${$selectedSite._id}`;
      const res = await fetch(url);

      if (!res.ok) {
        throw new Error('Failed to fetch site participants');
      }

      const responseData = await res.json();
      siteParticipants = responseData.data;
    } catch (err) {
      console.error('Error fetching site participants:', err);
      siteParticipants = [];
    }
  }

  function buildSubjectCgmStatsUrl() {
    const thresholds = $studyProperties?.glucoseParameters?.thresholds;
    const events = $studyProperties?.glucoseParameters?.events;
    const eventDuration = events?.eventDuration ?? 3;
    let url = `${API_BASE_URL}/stats/subjectCGMStats?page=1&pageSize=999999&eventDuration=${eventDuration}&inRange=true`;

    if ($selectedStudy?._id) {
      url += `&studyGUID=${$selectedStudy._id}`;
    }

    if (thresholds) {
      if (thresholds.veryLow != null) url += `&veryLow=${thresholds.veryLow}`;
      if (thresholds.low != null) url += `&low=${thresholds.low}`;
      if (thresholds.high != null) url += `&high=${thresholds.high}`;
      if (thresholds.veryHigh != null) url += `&veryHigh=${thresholds.veryHigh}`;
    }

    if (events) {
      if (events.hypoThreshold != null) url += `&hypoThreshold=${events.hypoThreshold}`;
      if (events.hyperThreshold != null) url += `&hyperThreshold=${events.hyperThreshold}`;
    }

    if ($selectedParticipant) {
      url += `&subjectGUIDs=${JSON.stringify([$selectedParticipant._id])}`;
    } else if (siteParticipants.length > 0) {
      // Exclude participants in 'Initiated' state
      const filteredParticipants = siteParticipants.filter((p) => p.statusLabel !== 'Initiated');
      const subjectIds = filteredParticipants.map((p) => p._id);
      url += `&subjectGUIDs=${JSON.stringify(subjectIds)}`;
    }

    return url;
  }

  async function fetchStats(page = 1) {
    try {
      lastStatsConfigKey = getStatsConfigKey();
      isLoading = true;
      const url = buildSubjectCgmStatsUrl();

      const res = await fetch(url);

      if (!res.ok) {
        throw new Error('Failed to fetch CGM stats');
      }

      const responseData = await res.json();

      // Sort the data by subjectId and lastRead
      const sortedData = responseData.data.sort((a, b) => {
        const participantA = $selectedParticipant
          ? $selectedParticipant
          : siteParticipants.find(p => p._id === a._id.subjectGUID);
        const participantB = $selectedParticipant
          ? $selectedParticipant
          : siteParticipants.find(p => p._id === b._id.subjectGUID);
        
        // First sort by subjectId
        const subjectIdCompare = (participantA?.subjectId || '').localeCompare(participantB?.subjectId || '');
        if (subjectIdCompare !== 0) return subjectIdCompare;
        
        // Then sort by lastRead in descending order
        return new Date(b.lastRead) - new Date(a.lastRead);
      });

      // Implement pagination on the sorted data
      resultCount = sortedData.length;
      totalPages = Math.ceil(resultCount / limit);
      const startIndex = (page - 1) * limit;
      stats = sortedData.slice(startIndex, startIndex + limit);
      currentPage = page;
    } catch (err) {
      console.error('Error fetching CGM stats:', err);
      stats = [];
    } finally {
      isLoading = false;
    }
  }
  function handleRefresh() {
    fetchStats(1);  }

  function handlePageChange(newPage) {
    if (newPage > 0 && newPage <= totalPages) {
      fetchStats(newPage);
    }
  }

  async function handleExportExcel() {
    try {
      isExporting = true;
      const url = buildSubjectCgmStatsUrl();

      const res = await fetch(url);

      if (!res.ok) {
        throw new Error('Failed to fetch all CGM stats');
      }

      const result = await res.json();
      
      // Sort the data by subjectId and lastRead (same as table)
      const sortedData = result.data.sort((a, b) => {
        const participantA = $selectedParticipant
          ? $selectedParticipant
          : siteParticipants.find(p => p._id === a._id.subjectGUID);
        const participantB = $selectedParticipant
          ? $selectedParticipant
          : siteParticipants.find(p => p._id === b._id.subjectGUID);
        const subjectIdCompare = (participantA?.subjectId || '').localeCompare(participantB?.subjectId || '');
        if (subjectIdCompare !== 0) return subjectIdCompare;
        return new Date(b.lastRead) - new Date(a.lastRead);
      });

      // Flatten the data structure for Excel export
      const flattenedData = sortedData.map(stat => {
        const participant = $selectedParticipant
          ? $selectedParticipant
          : siteParticipants.find(p => p._id === stat._id.subjectGUID);
        return {
          'Site ID': $selectedSite?.siteId || 'N/A',
          'Subject ID': participant?.subjectId || 'N/A',
          'Sensor': stat._id.externalId,
          'Readings': stat.total,
          'Completeness (%)': stat.completenessPercent,
          'Mean': convertGlucoseValue(stat.mean, $glucoseunit)?.toFixed(1) || 'N/A',
          'Unit': $glucoseunit,
          'GMI (%)': stat.gmi,
          'SD': stat.sd,
          'CV (%)': stat.cvPercent,
          'Very Low (%)': stat.veryLow.percent,
          'Very Low Count': stat.veryLow.count,
          'Low (%)': stat.low.percent,
          'Low Count': stat.low.count,
          'Target (%)': stat.target.percent,
          'Target Count': stat.target.count,
          'High (%)': stat.high.percent,
          'High Count': stat.high.count,
          'Very High (%)': stat.veryHigh.percent,
          'Very High Count': stat.veryHigh.count,
          'Hypo Events': stat.hypoEvents,
          'Hyper Events': stat.hyperEvents,
          'First Reading': formatToLocalDateTime(stat.firstRead, stat.timezone),
          'Last Reading': formatToLocalDateTime(stat.lastRead, stat.timezone),
          'Timezone': stat.timezone
        };
      });

      const filename = generateExportFilename({
        protocolNumber: $selectedStudy?.protocolNumber,
        siteId: $selectedSite?.siteId,
        reportType: 'CGM_Stats'
      });

      exportToExcel(flattenedData, filename);
    } catch (err) {
      console.error('Error exporting CGM stats:', err);
    } finally {
      isExporting = false;
    }
  }

  function getRowKey(stat) {
    return `${stat._id.subjectGUID}-${stat._id.externalId}`;
  }

  function toggleAllRows() {
    const allChecked = stats.every(stat => checkedRows[getRowKey(stat)]);
    const newCheckedRows = {};
    
    if (allChecked) {
      // If all are checked, uncheck all
      stats.forEach(stat => {
        newCheckedRows[getRowKey(stat)] = false;
      });
    } else {
      // If not all are checked, check all
      stats.forEach(stat => {
        newCheckedRows[getRowKey(stat)] = true;
      });
    }
    
    checkedRows = newCheckedRows;
  }

  function toggleShowOnlyChecked() {
    showOnlyChecked = !showOnlyChecked;
  }

  function isRowChecked(stat) {
    return !!checkedRows[getRowKey(stat)];
  }

  // Check if all rows are selected
  $: allRowsChecked = stats.length > 0 && stats.every(stat => checkedRows[getRowKey(stat)]);
  
  function handleRowClick(stat) {
    if (event.ctrlKey || event.metaKey) {
      // Ctrl/Cmd + click to toggle baseline
      baselineRow = baselineRow === stat ? null : stat;
    } else {
      selectedRow = stat;
    }
  }

  // Filter stats based on showOnlyChecked
  $: filteredStats = showOnlyChecked 
    ? stats.filter(stat => isRowChecked(stat))
    : stats;
</script>

<!-- Add loading overlay for export -->
{#if isExporting}
  <div class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div class="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-lg flex flex-col items-center">
      <div class="loader mb-4"></div>
      <p class="text-sm text-gray-700 dark:text-gray-300">Preparing Excel export...</p>
    </div>
  </div>
{/if}

<StudyHeader />

<FeatureInfo 
  title="CGM Statistics" 
  description="This page displays detailed CGM statistics for the selected participant. You can view sensor information, glucose ranges, 
  and key metrics like GMI, CV%, and hypo/hyper events. Use the Excel export function to download the complete dataset for further analysis.
  To compare select sensors, use the filter button to show only checked rows.
  
  *Metrics*
  *Data Completeness*: % of possible readings captured Especially relevant for missing CGM data
  *Mean Glucose*: Average glucose value over the period
  *GMI* (Glucose Management Indicator): Estimated HbA1c from mean glucose GMI = 3.31 + 0.02392 x mean glucose
  *SD* (Standard Deviation): Shows how much glucose values vary from the average. Lower SD means more stable glucose levels. A high SD means more fluctuations and may suggest more frequent highs and lows.
  *CV%* (Glucose Variability): Coefficient of variation: SD / mean x 100 (Target: CV% <36%)
  *TBR* (Time Below Range): % of readings <{$studyProperties?.glucoseParameters?.thresholds?.low || 70} mg/dL, split into: TBR1 ({$studyProperties?.glucoseParameters?.thresholds?.veryLow || 54}-{$studyProperties?.glucoseParameters?.thresholds?.low - 1 || 69}) and TBR2 (<{$studyProperties?.glucoseParameters?.thresholds?.veryLow || 54}) (Indicates hypoglycemia)
  *TIR* (Time in Range): % of readings in {$studyProperties?.glucoseParameters?.thresholds?.low || 70}-{$studyProperties?.glucoseParameters?.thresholds?.high || 180} mg/dL (Core outcome)
  *TAR* (Time Above Range): % of readings >{$studyProperties?.glucoseParameters?.thresholds?.high || 180} mg/dL, split into: TAR1 ({$studyProperties?.glucoseParameters?.thresholds?.high + 1 || 181}-{$studyProperties?.glucoseParameters?.thresholds?.veryHigh || 250}) and TAR2 (>{$studyProperties?.glucoseParameters?.thresholds?.veryHigh || 250}) (Indicates hyperglycemia)
  *#Hypo* (Number of Hypo Events): Count of episodes below thresholds e.g., # of times <{$studyProperties?.glucoseParameters?.events?.hypoThreshold || 54} mg/dL for >{($studyProperties?.glucoseParameters?.events?.eventDuration || 3) * 5} mins
  *#Hyper* (Number of Hyper Events): Count of episodes above thresholds e.g., # of times >{$studyProperties?.glucoseParameters?.events?.hyperThreshold || 250} mg/dL for >{($studyProperties?.glucoseParameters?.events?.eventDuration || 3) * 5} mins
  "
  marginBottom="mb-0"
/>

{#if $selectedParticipant}
  <ParticipantDetails showOptionsMenu={false} />
{:else if $selectedSite}
  <SiteDetails showOptionsMenu={false} />
{/if}

<!-- Add Infographic Cards -->
{#if isCgmStatisticsReportCardViewEnabled}
  <CgmStatsInfoCards {stats} {selectedRow} {baselineRow} studyProperties={$studyProperties} />
{/if}

<!-- Title and Filters -->
<Toolbar title="CGM Statistics" itemCountTitle="Sensors" itemCount={resultCount}>
  <div class="text-m flex items-center gap-4">
    <div class="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
      <div class="flex items-center gap-1">
        <div class="w-1 h-3 bg-orange-500"></div>
        <span>Selected</span>
      </div>
      <div class="flex items-center gap-1">
        <div class="w-1 h-3 bg-sky-500"></div>
        <span>Baseline</span>
      </div>
      <span class="mx-1">|</span>
      <span>Ctrl/Cmd + click to toggle baseline</span>
    </div>
    <div class="flex text-xs items-baseline gap-2">
      <button 
        on:click={toggleShowOnlyChecked} 
        class="pagination mx-1 p-0 {showOnlyChecked ? 'text-blue-600' : ''}" 
        title={showOnlyChecked ? "Show all rows" : "Show only checked rows"}
      >
        <FontAwesomeIcon icon={faFilter} />
      </button>      <button on:click={handleRefresh} class="pagination mx-1 p-0" title="Refresh">
        <FontAwesomeIcon icon={faRotate} />
      </button>
      <!-- <PrintButton /> -->
    </div>
  </div>
</Toolbar>

{#if isLoading}
  <div class="flex flex-col justify-center items-center h-64 bg-stone-100/50 dark:bg-gray-725 mx-1">
    <div class="loader mb-4"></div>
    <p class="text-xs justify-center text-gray-700 dark:text-gray-300">
      preparing CGM statistics report
    </p>
  </div>
{:else}
<div class="w-full">
  <div class="px-1 pt-0 overflow-x-auto">
    <div class="min-w-full inline-block align-middle">
      <div class="relative">
        <table class="min-w-full border-collapse table-auto">
          <thead>
            <tr class="text-sm text-black dark:text-slate-300 bg-stone-200/50 dark:bg-gray-800/90">
              <th class="px-4 py-2 text-left bold sticky left-0 z-40 bg-stone-200 dark:bg-gray-800">Site</th>
              <th class="px-4 py-2 text-left bold sticky left-[100px] z-30 bg-stone-200 dark:bg-gray-800" style="width:100px; min-width:100px; max-width:200px;">Subject</th>
              <th class="px-4 py-2 text-left bold">Sensor</th>
              <th class="px-4 py-2 text-left bold">Readings</th>
              <th class="px-4 py-2 text-left bold">Comp.</th>
              <th class="px-4 py-2 text-left bold">Mean</th>
              <th class="px-4 py-2 text-left bold">GMI</th>
              <th class="px-4 py-2 text-left bold">SD</th>
              <th class="px-4 py-2 text-left bold">CV%</th>
              <th class="px-4 py-2 text-left bold">TBR2</th>
              <th class="px-4 py-2 text-left bold">TBR1</th>
              <th class="px-4 py-2 text-left bold">TIR</th>
              <th class="px-4 py-2 text-left bold">TAR1</th>
              <th class="px-4 py-2 text-left bold">TAR2</th>
              <th class="px-4 py-2 text-left bold">#Hypo</th>
              <th class="px-4 py-2 text-left bold">#Hyper</th>
              <th class="px-4 py-2 text-left bold">First (Local)</th>
              <th class="px-4 py-2 text-left bold">Last (Local)</th>
              <th class="px-4 py-2 text-left bold sticky right-0 z-30 bg-stone-200 dark:bg-gray-800">
                <input 
                  type="checkbox" 
                  class="form-checkbox h-4 w-4 text-blue-600"
                  checked={allRowsChecked}
                  on:change={toggleAllRows}
                />
              </th>
            </tr>
          </thead>
          <tbody>
            {#each filteredStats as stat, index}
              {@const participant = siteParticipants.find(p => p._id === stat._id.subjectGUID)}
              {@const rowKey = getRowKey(stat)}
              <tr
                class="text-sm hover:bg-white dark:hover:bg-gray-775 text-gray-800 dark:text-slate-300 {index %
                  2 ===
                0
                  ? 'bg-stone-50 dark:bg-gray-725'
                  : 'bg-stone-75 dark:bg-gray-750'}"
                on:click={() => handleRowClick(stat)}
                role="button"
                tabindex="0"
              >
                <td class="px-4 py-2 min-w-24 sticky left-0 z-40 {
                  selectedRow === stat ? 'bg-sky-200 dark:bg-sky-700 selection-marker-orange' : 
                  baselineRow === stat ? 'bg-sky-200 dark:bg-sky-700 selection-marker-blue' : 
                  index % 2 === 0 ? 'bg-stone-50 dark:bg-gray-725' : 'bg-stone-75 dark:bg-gray-750'
                } shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]">{$selectedSite?.siteId || 'N/A'}</td>
                <td class="px-4 py-2 whitespace-nowrap sticky left-[100px] z-30 {index % 2 === 0 ? 'bg-stone-50 dark:bg-gray-725' : 'bg-stone-75 dark:bg-gray-750'}" style="width:100px; min-width:100px; max-width:200px;">{participant?.subjectId || 'N/A'}</td>
                <td class="px-4 py-2 whitespace-nowrap">{stat._id.externalId}</td>
                <td class="px-4 py-2 min-w-24">{stat.total}</td>
                <td class="px-4 py-2 min-w-24">{stat.completenessPercent}%</td>
                <td class="px-4 py-2 min-w-32">{convertGlucoseValue(stat.mean, $glucoseunit)?.toFixed(1) || 'N/A'} {$glucoseunit}</td>
                <td class="px-4 py-2 min-w-24">{stat.gmi}%</td>
                <td class="px-4 py-2 min-w-32">{convertGlucoseValue(stat.sd, $glucoseunit)?.toFixed(1) || 'N/A'} {$glucoseunit}</td>
                <td class="px-4 py-2 min-w-24">{stat.cvPercent !== null && stat.cvPercent !== undefined ? stat.cvPercent : 'N/A'}%</td>
                <td class="px-4 py-2 min-w-36">{stat.veryLow.percent}% ({stat.veryLow.count})</td>
                <td class="px-4 py-2 min-w-36">{stat.low.percent}% ({stat.low.count})</td>
                <td class="px-4 py-2 min-w-36">{stat.target.percent}% ({stat.target.count})</td>
                <td class="px-4 py-2 min-w-36">{stat.high.percent}% ({stat.high.count})</td>
                <td class="px-4 py-2 min-w-36">{stat.veryHigh.percent}% ({stat.veryHigh.count})</td>
                <td class="px-4 py-2 min-w-24">{stat.hypoEvents}</td>
                <td class="px-4 py-2 min-w-24">{stat.hyperEvents}</td>
                <td class="px-4 py-2 min-w-56">{formatToLocalDateTime(stat.firstRead, stat.timezone)}</td>
                <td class="px-4 py-2 min-w-56">{formatToLocalDateTime(stat.lastRead, stat.timezone)}</td>
                <td class="px-4 py-2 min-w-8 sticky right-0 z-20 {index % 2 === 0 ? 'bg-stone-50 dark:bg-gray-725' : 'bg-stone-75 dark:bg-gray-750'}">
                  <input 
                    type="checkbox" 
                    class="form-checkbox h-4 w-4 text-blue-600"
                    bind:checked={checkedRows[rowKey]}
                  />
                </td>
              </tr>
            {/each}
          </tbody>
        </table>
      </div>
    </div>
  </div>
</div>
{/if}

<!-- Add Control Buttons -->
<Footer>
  <Pagination {currentPage} {totalPages} onPageChange={handlePageChange} />
  <div>
    <button
      on:click={handleExportExcel}
      class="bg-stone-300 dark:bg-gray-600 text-black dark:text-white py-1 px-4 rounded-full text-xs"
      >Excel</button
    >
  </div>
</Footer>

<style>
  .loader {
    border: 2px solid #20669c;
    border-radius: 50%;
    border-top: 4px solid #98acb4;
    width: 32px;
    height: 32px;
    animation: spin 2s linear infinite;
  }

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }

  /* Hide scrollbar for Chrome, Safari and Opera */
  .overflow-x-auto::-webkit-scrollbar {
    display: none;
  }

  /* Hide scrollbar for IE, Edge and Firefox */
  .overflow-x-auto {
    -ms-overflow-style: none;  /* IE and Edge */
    scrollbar-width: none;  /* Firefox */
  }

  /* Add shadow to frozen columns */
  .sticky {
    box-shadow: 2px 0 5px -2px rgba(0, 0, 0, 0.1);
  }

  /* Selection marker styles */
  .selection-marker-orange::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 4px;
    background-color: #f97316; /* orange-500 */
    z-index: 50;
  }  .selection-marker-blue::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 4px;
    background-color: #3b82f6; /* blue-500 */
    z-index: 50;
  }

  /* Print styles */
  @media print {
    canvas {
      background-color: white !important;
      -webkit-print-color-adjust: exact;
      color-adjust: exact;
    }
  }
</style>
